import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface BrandProductTag3 {
  id: string;
  brand_id: string;
  name: string;
  description?: string;
  color: string;
  created_at: string;
  updated_at: string;
}

export const useBrandProductTag3 = (brandId: string) => {
  const queryClient = useQueryClient();

  const { data: tags, isLoading, error } = useQuery({
    queryKey: ['brand-product-tag3', brandId],
    queryFn: async (): Promise<BrandProductTag3[]> => {
      const { data, error } = await supabase
        .from('brand_product_tag3')
        .select('*')
        .eq('brand_id', brandId)
        .order('name', { ascending: true });

      if (error) throw error;
      return data || [];
    },
    enabled: !!brandId,
  });

  const createTagMutation = useMutation({
    mutationFn: async (newTag: Omit<BrandProductTag3, 'id' | 'created_at' | 'updated_at'>) => {
      const { data, error } = await supabase
        .from('brand_product_tag3')
        .insert(newTag)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brand-product-tag3', brandId] });
      toast.success('Tag3 created successfully');
    },
    onError: (error) => {
      console.error('Error creating tag3:', error);
      toast.error('Failed to create tag3');
    },
  });

  const updateTagMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<BrandProductTag3> }) => {
      const { data, error } = await supabase
        .from('brand_product_tag3')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brand-product-tag3', brandId] });
      toast.success('Tag3 updated successfully');
    },
    onError: (error) => {
      console.error('Error updating tag3:', error);
      toast.error('Failed to update tag3');
    },
  });

  const deleteTagMutation = useMutation({
    mutationFn: async (tagId: string) => {
      // First, remove this tag from all products
      const { data: productsWithTag, error: fetchError } = await supabase
        .from('brand_products_hooks')
        .select('id, tag3')
        .eq('brand_id', brandId)
        .contains('tag3', [tagId]);

      if (fetchError) throw fetchError;

      if (productsWithTag && productsWithTag.length > 0) {
        for (const product of productsWithTag) {
          const updatedTags = (product.tag3 as string[]).filter(id => id !== tagId);
          const { error: updateError } = await supabase
            .from('brand_products_hooks')
            .update({ tag3: updatedTags })
            .eq('id', product.id);

          if (updateError) throw updateError;
        }
      }

      // Then delete the tag
      const { error } = await supabase
        .from('brand_product_tag3')
        .delete()
        .eq('id', tagId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brand-product-tag3', brandId] });
      queryClient.invalidateQueries({ queryKey: ['brand-products-hooks', brandId] });
      toast.success('Tag3 deleted successfully');
    },
    onError: (error) => {
      console.error('Error deleting tag3:', error);
      toast.error('Failed to delete tag3');
    },
  });

  const createTag = createTagMutation.mutate;
  const updateTag = updateTagMutation.mutate;
  const deleteTag = deleteTagMutation.mutate;

  return {
    tags,
    isLoading,
    error,
    createTag,
    updateTag,
    deleteTag,
    isCreating: createTagMutation.isPending,
    isUpdating: updateTagMutation.isPending,
    isDeleting: deleteTagMutation.isPending,
  };
};
